import { deviceView, type TemplateNode } from "../builder";
import type { LibraryTemplate } from "../library";
import {
  DEVICE_SHADOW,
  type DeviceSpec,
  heightAt,
  LAPTOP,
  PHONE,
  storeBadge,
  widthAt,
} from "./mockups";
import {
  BLACK,
  captions,
  cornerLogo,
  device,
  during,
  ellipse,
  enter,
  icon,
  logo,
  ON_VIDEO,
  parts,
  pill,
  REEL_CAPTIONS,
  rect,
  stack,
  star,
  t,
  text,
} from "./shorthands";

/**
 * Reels (9:16) with device mockups (docs/19-mockups.md): a screenshot or a
 * screen recording dragged onto a device fills its screen (a video becomes
 * a clip in the device). Most sit on a brand colour; "Bildschirm & Gesicht"
 * puts the phone over the main video. Twins without a device live in
 * `reels-more.ts` / `reels.ts`.
 */

const phone = (view: string, id = PHONE): DeviceSpec => ({
  device: id,
  tilt: deviceView(id, view),
});
const PHONE_FRONT = phone("front");
const PHONE_HERO = phone("hero");
const PHONE_FLOAT = phone("float");
/** Stronger shadow for large devices on dark backgrounds. */
const DEEP_SHADOW = { color: "#00000080", y: 40, blur: 90 };

/** Horizontally centred x of a device of height `h`. */
const centred = (spec: DeviceSpec, h: number) => (1080 - widthAt(spec, h)) / 2;

/**
 * Soft circles in the corners, clear of the devices: a video dragged onto a
 * device becomes a clip, and clips lie below all elements.
 */
const circles = (fill: string, second: string): TemplateNode[] => [
  ellipse({
    x: 560,
    y: -320,
    w: 840,
    h: 840,
    fill,
    name: "Kreis",
    ...enter("fade", { duration: 800 }),
  }),
  ellipse({
    x: -150,
    y: 140,
    w: 380,
    h: 380,
    fill: second,
    name: "Kreis",
    ...enter("fade", { duration: 1200 }),
  }),
];

const TABLET = "ipad-air-11";
const TABLET_HERO: DeviceSpec = { device: TABLET, tilt: deviceView(TABLET, "hero") };
const LAPTOP_LEFT: DeviceSpec = { device: LAPTOP, tilt: deviceView(LAPTOP, "left") };
const LAPTOP_FRONT: DeviceSpec = { device: LAPTOP, tilt: deviceView(LAPTOP, "front") };
const MONITOR: DeviceSpec = { device: "monitor-27", tilt: deviceView("monitor-27", "front") };
const BROWSER: DeviceSpec = { device: "browser", url: "www.beispiel.de" };

export const REEL_MOCKUPS: LibraryTemplate[] = [
  {
    id: "reel-website-launch-mockup",
    title: "Reel: Neue Website mit Mockup",
    description: "Browserfenster und Handy fliegen ein, Schlagzeile und Adresse pulsieren",
    presetId: "reel",
    topic: "ankuendigung",
    style: "klar",
    tags: ["mockup", "website", "webseite", "relaunch", "browser", "online", "launch", "homepage"],
    pages: [
      {
        background: "primary",
        nodes: [
          ...circles("on:primary/10", "on:primary/10"),
          stack({
            x: 80,
            y: 250,
            w: 920,
            align: "center",
            gap: 14,
            items: [
              t("RELAUNCH", "subline", 36, "on:primary/80", enter("fade", { duration: 500 })),
              t(
                "Unsere neue Website ist online",
                "heading-1",
                90,
                "on:primary",
                enter("slide-up", { duration: 600 }),
              ),
            ],
          }),
          ...pill(260, 600, 560, 88, "www.beispiel.de", "accent", 36, {
            ...enter("pop", { duration: 1400, loop: "pulse" }),
          }),
          device({
            ...BROWSER,
            x: 70,
            y: 760,
            w: 900,
            shadow: DEVICE_SHADOW,
            name: "Browser",
            ...enter("fly-up", { duration: 800 }),
          }),
          device({
            ...PHONE_FRONT,
            x: 740,
            y: 1020,
            h: 560,
            shadow: DEVICE_SHADOW,
            name: "iPhone",
            ...enter("fly-left", { duration: 1100 }),
          }),
        ],
      },
    ],
  },
  {
    id: "reel-app-anleitung-mockup",
    title: "Reel: App-Anleitung mit Handy",
    description:
      "Bildschirmaufnahme im Handy, vier Schritte erscheinen abwechselnd links und rechts",
    presetId: "reel",
    topic: "wissen",
    style: "verspielt",
    tags: [
      "mockup",
      "app",
      "anleitung",
      "tutorial",
      "bildschirmaufnahme",
      "screen recording",
      "handy",
      "schritte",
    ],
    previewTime: 1500,
    pages: [
      {
        background: "surface",
        nodes: [
          ...circles("primary/20", "accent/20"),
          stack({
            x: 80,
            y: 250,
            w: 920,
            align: "center",
            gap: 12,
            items: [
              t("SO GEHT’S", "subline", 34, "primary", enter("fade")),
              t("Profilbild ändern in 4 Schritten", "heading-1", 72, "text", enter("slide-up")),
            ],
          }),
          device({
            ...PHONE_FRONT,
            x: centred(PHONE_FRONT, 1060),
            y: 560,
            h: 1060,
            shadow: DEVICE_SHADOW,
            name: "iPhone",
            ...enter("fly-up", { duration: 800 }),
          }),
          ...parts(4, 1000, 3500).flatMap(({ index, start, end }) => {
            const steps = ["Einstellungen", "Profil antippen", "Foto wählen", "Speichern"];
            const left = index % 2 === 0;
            return pill(
              left ? 40 : 640,
              720 + index * 170,
              400,
              90,
              `${index + 1} · ${steps[index]}`,
              "primary",
              34,
              {
                rotation: left ? -3 : 3,
                ...during(start, end, left ? "slide-right" : "slide-left", { duration: 400 }),
              },
            );
          }),
        ],
      },
    ],
  },
  {
    id: "reel-app-neue-funktion",
    title: "Reel: Neue Funktion in der App",
    description: "Großes Handy schräg mit Glanz, „Neue Funktion“ und zwei Vorteile als Chips",
    presetId: "reel",
    topic: "produkt",
    style: "nacht",
    tags: ["mockup", "app", "update", "feature", "neu", "funktion", "handy", "iphone"],
    pages: [
      {
        background: "background",
        nodes: [
          ...circles("primary/25", "accent/20"),
          icon({
            icon: "sparkle",
            x: 480,
            y: 190,
            w: 120,
            h: 120,
            color: "accent",
            name: "Funkeln",
            ...enter("spin", { duration: 700, loop: "pulse" }),
          }),
          stack({
            x: 80,
            y: 320,
            w: 920,
            align: "center",
            gap: 14,
            items: [
              t("NEUE FUNKTION", "subline", 36, "accent", enter("fade")),
              t(
                "Jetzt mit Dunkelmodus",
                "heading-1",
                96,
                "text",
                enter("slide-up", { duration: 600 }),
              ),
            ],
          }),
          ...pill(
            110,
            640,
            410,
            80,
            "Schont die Augen",
            "primary",
            32,
            enter("pop", { duration: 1100 }),
          ),
          ...pill(560, 640, 410, 80, "Spart Akku", "accent", 32, enter("pop", { duration: 1400 })),
          device({
            ...PHONE_HERO,
            x: centred(PHONE_HERO, 1040),
            y: 780,
            h: 1040,
            shadow: DEEP_SHADOW,
            glare: true,
            name: "iPhone",
            ...enter("fly-up", { duration: 900 }),
          }),
        ],
      },
    ],
  },
  {
    id: "reel-app-download",
    title: "Reel: App laden",
    description: "Schwebendes Handy, fünf Sterne, Bewertung und beide Store-Badges",
    presetId: "reel",
    topic: "produkt",
    style: "laut",
    tags: ["mockup", "app", "download", "app store", "google play", "bewertung", "sterne", "handy"],
    pages: [
      {
        background: "primary",
        nodes: [
          ...circles("on:primary/10", "accent/30"),
          ...[0, 1, 2, 3, 4].map((index) =>
            icon({
              icon: "star",
              x: 290 + index * 100,
              y: 250,
              w: 80,
              h: 80,
              color: "accent",
              name: `Stern ${index + 1}`,
              ...enter("pop", { duration: 400 + index * 150 }),
            }),
          ),
          stack({
            x: 80,
            y: 350,
            w: 920,
            align: "center",
            gap: 12,
            items: [
              t(
                "Jetzt kostenlos laden",
                "heading-1",
                96,
                "on:primary",
                enter("slide-up", { duration: 600 }),
              ),
              t(
                "4,8 von 5 Sternen · über 2.000 Bewertungen",
                "body",
                34,
                "on:primary/85",
                enter("fade", { duration: 900 }),
              ),
            ],
          }),
          ...storeBadge(265, 640, "apple").map((node) => ({
            ...node,
            ...enter("slide-up", { duration: 1100 }),
          })),
          ...storeBadge(555, 640, "google").map((node) => ({
            ...node,
            ...enter("slide-up", { duration: 1300 }),
          })),
          device({
            ...PHONE_FLOAT,
            x: centred(PHONE_FLOAT, 980),
            y: 800,
            h: 980,
            shadow: DEEP_SHADOW,
            name: "iPhone",
            ...enter("fly-up", { duration: 900, loop: "float" }),
          }),
        ],
      },
    ],
  },
  {
    id: "reel-online-shop-mockup",
    title: "Reel: Online-Shop mit Handy",
    description: "Shop im Handy, wackelnder Preis-Sticker und pulsierender Button „Zum Shop“",
    presetId: "reel",
    topic: "angebot",
    style: "warm",
    tags: [
      "mockup",
      "shop",
      "online-shop",
      "e-commerce",
      "bestellen",
      "preis",
      "handy",
      "verkaufen",
    ],
    pages: [
      {
        background: "surface",
        nodes: [
          ellipse({
            x: 520,
            y: -260,
            w: 560,
            h: 560,
            fill: "primary/25",
            name: "Kreis",
            ...enter("fade", { duration: 800 }),
          }),
          logo({ x: 80, y: 240, w: 240, h: 66, on: "surface" }),
          stack({
            x: 80,
            y: 350,
            w: 920,
            gap: 14,
            items: [
              t(
                "Jetzt online bestellen",
                "heading-1",
                96,
                "text",
                enter("slide-up", { duration: 600 }),
              ),
              t(
                "Versand in 24 Stunden · ab 30 € versandkostenfrei",
                "body",
                34,
                "text-muted",
                enter("fade", { duration: 900 }),
              ),
            ],
          }),
          device({
            ...PHONE_HERO,
            x: 100,
            y: 700,
            h: 980,
            shadow: DEVICE_SHADOW,
            name: "iPhone",
            ...enter("fly-right", { duration: 800 }),
          }),
          star({
            x: 640,
            y: 760,
            w: 330,
            h: 330,
            sides: 14,
            innerRatio: 0.82,
            rotation: 8,
            fill: "accent",
            name: "Preis-Sticker",
            ...enter("pop", { duration: 1100, loop: "wiggle" }),
          }),
          text({
            text: "ab 19,90 €",
            style: "heading-1",
            size: 64,
            color: "on:accent",
            x: 680,
            y: 760,
            w: 250,
            h: 330,
            rotation: 8,
            align: "center",
            ...enter("pop", { duration: 1100, loop: "wiggle" }),
          }),
          icon({
            icon: "shopping-cart",
            x: 580,
            y: 1180,
            w: 90,
            h: 90,
            color: "primary",
            name: "Warenkorb",
            ...enter("pop", { duration: 1300 }),
          }),
          ...pill(560, 1300, 440, 100, "Zum Shop", "primary", 40, {
            ...enter("pop", { duration: 1500, loop: "pulse" }),
          }),
        ],
      },
    ],
  },
  {
    id: "reel-speisekarte-tablet",
    title: "Reel: Digitale Speisekarte",
    description: "Tablet schräg mit der neuen Karte, Besteck-Symbol und Hinweis zum Bestellen",
    presetId: "reel",
    topic: "produkt",
    style: "natur",
    tags: ["mockup", "speisekarte", "restaurant", "gastro", "tablet", "ipad", "bestellen", "menü"],
    pages: [
      {
        background: "primary",
        nodes: [
          ...circles("on:primary/10", "accent/25"),
          icon({
            icon: "fork-knife",
            x: 480,
            y: 230,
            w: 120,
            h: 120,
            color: "accent",
            name: "Besteck",
            ...enter("drop", { duration: 700 }),
          }),
          stack({
            x: 80,
            y: 380,
            w: 920,
            align: "center",
            gap: 14,
            items: [
              t(
                "Unsere Karte – jetzt digital",
                "heading-1",
                88,
                "on:primary",
                enter("slide-up", { duration: 600 }),
              ),
              t(
                "QR-Code am Tisch scannen und direkt bestellen",
                "body",
                36,
                "on:primary/85",
                enter("fade", { duration: 900 }),
              ),
            ],
          }),
          device({
            ...TABLET_HERO,
            x: centred(TABLET_HERO, 940),
            y: 740,
            h: 940,
            shadow: DEEP_SHADOW,
            name: "iPad",
            ...enter("zoom", { duration: 900 }),
          }),
        ],
      },
    ],
  },
  {
    id: "reel-online-kurs-laptop",
    title: "Reel: Online-Kurs & Webinar",
    description: "Laptop mit Kursvorschau, Termin-Chip und pulsierendem „Jetzt anmelden“",
    presetId: "reel",
    topic: "event",
    style: "klar",
    tags: [
      "mockup",
      "webinar",
      "online-kurs",
      "kurs",
      "laptop",
      "macbook",
      "anmeldung",
      "workshop",
    ],
    pages: [
      {
        background: "background",
        nodes: [
          ...circles("primary/15", "accent/20"),
          stack({
            x: 80,
            y: 250,
            w: 920,
            align: "center",
            gap: 14,
            items: [
              t("ONLINE-KURS", "subline", 36, "primary", enter("fade")),
              t(
                "Social Media für Einsteiger",
                "heading-1",
                92,
                "text",
                enter("slide-up", { duration: 600 }),
              ),
            ],
          }),
          ...pill(
            240,
            600,
            600,
            80,
            "Di, 14. Oktober · 18 Uhr",
            "accent",
            32,
            enter("pop", { duration: 1000 }),
          ),
          device({
            ...LAPTOP_LEFT,
            x: 40,
            y: 740,
            w: 1000,
            shadow: DEVICE_SHADOW,
            name: "MacBook",
            ...enter("zoom", { duration: 900 }),
          }),
          ...pill(
            290,
            740 + heightAt(LAPTOP_LEFT, 1000) + 30,
            500,
            100,
            "Jetzt anmelden",
            "primary",
            40,
            {
              ...enter("pop", { duration: 1400, loop: "pulse" }),
            },
          ),
        ],
      },
    ],
  },
  {
    id: "reel-redesign-vorher-nachher",
    title: "Reel: App vorher / nachher",
    description: "Zwei Handys nebeneinander – das alte Design fliegt ein, dann das neue",
    presetId: "reel",
    topic: "produkt",
    style: "klar",
    tags: ["mockup", "vorher nachher", "redesign", "app", "vergleich", "handy", "neues design"],
    previewTime: 1800,
    pages: [
      {
        background: "surface",
        nodes: [
          ellipse({
            x: 520,
            y: -260,
            w: 560,
            h: 560,
            fill: "primary/20",
            name: "Kreis",
            ...enter("fade", { duration: 800 }),
          }),
          stack({
            x: 80,
            y: 250,
            w: 920,
            align: "center",
            gap: 12,
            items: [
              t("REDESIGN", "subline", 34, "primary", enter("fade")),
              t(
                "Unsere App im neuen Look",
                "heading-1",
                88,
                "text",
                enter("slide-up", { duration: 600 }),
              ),
            ],
          }),
          ...pill(90, 600, 300, 76, "VORHER", "text", 30, enter("fade", { duration: 700 })),
          device({
            ...PHONE_FRONT,
            tilt: { ...PHONE_FRONT.tilt, z: -4 },
            x: 60,
            y: 720,
            h: 820,
            shadow: DEVICE_SHADOW,
            opacity: 0.85,
            name: "iPhone vorher",
            ...enter("fly-right", { duration: 700 }),
          }),
          ...pill(690, 600, 300, 76, "NACHHER", "primary", 30, enter("pop", { duration: 1600 })),
          device({
            ...PHONE_FRONT,
            tilt: { ...PHONE_FRONT.tilt, z: 4 },
            x: 1020 - widthAt(PHONE_FRONT, 820),
            y: 720,
            h: 820,
            shadow: DEVICE_SHADOW,
            name: "iPhone nachher",
            ...enter("fly-left", { duration: 1600 }),
          }),
          icon({
            icon: "sparkle",
            x: 960,
            y: 590,
            w: 80,
            h: 80,
            color: "accent",
            name: "Funkeln",
            ...enter("pop", { duration: 1900, loop: "wiggle" }),
          }),
        ],
      },
    ],
  },
  {
    id: "reel-bildschirm-gesicht",
    title: "Reel: Bildschirm & Gesicht",
    description: "Handy mit Bildschirmaufnahme über deinem Video, Hinweis-Hand und Untertitel",
    presetId: "reel",
    topic: "wissen",
    style: "nacht",
    tags: [
      "mockup",
      "bildschirmaufnahme",
      "screen recording",
      "erklären",
      "reaktion",
      "green screen",
      "handy",
      "talking head",
    ],
    pages: [
      {
        background: BLACK,
        nodes: [
          cornerLogo(),
          device({
            ...PHONE_FRONT,
            tilt: { ...PHONE_FRONT.tilt, z: -3 },
            x: 70,
            y: 300,
            h: 780,
            shadow: DEEP_SHADOW,
            name: "iPhone",
            ...enter("fly-right", { duration: 700 }),
          }),
          ...pill(
            530,
            360,
            470,
            76,
            "ICH ZEIG’S DIR",
            "accent",
            30,
            enter("slide-left", { duration: 600 }),
          ),
          text({
            text: "So buchst du in 30 Sekunden",
            style: "heading-2",
            size: 62,
            x: 530,
            y: 470,
            w: 480,
            ...ON_VIDEO,
            ...enter("slide-left", { duration: 800 }),
          }),
          icon({
            icon: "hand-pointing",
            x: 560,
            y: 820,
            w: 130,
            h: 130,
            color: "accent",
            rotation: 90,
            name: "Hinweis-Hand",
            shadow: { y: 6, blur: 18, color: "#00000080" },
            ...enter("pop", { duration: 1000, loop: "wiggle" }),
          }),
          captions(REEL_CAPTIONS),
        ],
      },
    ],
  },
  {
    id: "reel-android-app",
    title: "Reel: Jetzt auch für Android",
    description: "Zwei Android-Handys schwebend, Schlagzeile und Google-Play-Badge",
    presetId: "reel",
    topic: "ankuendigung",
    style: "verspielt",
    tags: ["mockup", "android", "app", "google play", "pixel", "galaxy", "samsung", "handy"],
    pages: [
      {
        background: "secondary",
        nodes: [
          ...circles("primary/35", "accent/25"),
          stack({
            x: 80,
            y: 250,
            w: 920,
            align: "center",
            gap: 14,
            items: [
              t("ENDLICH", "subline", 36, "accent", enter("fade")),
              t(
                "Jetzt auch für Android",
                "heading-1",
                96,
                "on:secondary",
                enter("slide-up", { duration: 600 }),
              ),
            ],
          }),
          ...storeBadge(410, 580, "google").map((node) => ({
            ...node,
            ...enter("pop", { duration: 1200 }),
          })),
          device({
            ...phone("float", "galaxy-s25"),
            x: 40,
            y: 760,
            h: 900,
            shadow: DEEP_SHADOW,
            name: "Galaxy",
            ...enter("fly-right", { duration: 800, loop: "float" }),
          }),
          device({
            ...phone("float", "pixel-10-pro"),
            x: 1040 - widthAt(phone("float", "pixel-10-pro"), 900),
            y: 860,
            h: 900,
            shadow: DEEP_SHADOW,
            name: "Pixel",
            ...enter("fly-left", { duration: 1100, loop: "float" }),
          }),
        ],
      },
    ],
  },
  {
    id: "reel-kundenprojekt",
    title: "Reel: Kundenprojekt (Case Study)",
    description: "Laptop und Handy mit dem Projekt, Ergebnisse als Chips – für Agenturen",
    presetId: "reel",
    topic: "produkt",
    style: "nacht",
    tags: [
      "mockup",
      "agentur",
      "portfolio",
      "case study",
      "referenz",
      "kundenprojekt",
      "webdesign",
      "laptop",
    ],
    previewTime: 2500,
    pages: [
      {
        background: "background",
        nodes: [
          ...circles("primary/25", "accent/15"),
          logo({ x: 80, y: 230, w: 240, h: 60, on: "background" }),
          stack({
            x: 80,
            y: 320,
            w: 920,
            gap: 12,
            items: [
              t("KUNDENPROJEKT", "subline", 32, "accent", enter("fade")),
              t(
                "Neue Website für Bäckerei Sonnenschein",
                "heading-1",
                76,
                "text",
                enter("slide-up", { duration: 600 }),
              ),
            ],
          }),
          ...["+40 % Anfragen", "2× schneller", "100 % mobil"].flatMap((result, index) =>
            pill(80 + index * 315, 640, 290, 76, result, index === 0 ? "accent" : "primary", 30, {
              ...during(1200 + index * 700, 16000, "pop", { out: null }),
            }),
          ),
          device({
            ...LAPTOP_FRONT,
            x: 60,
            y: 800,
            w: 900,
            shadow: DEEP_SHADOW,
            name: "MacBook",
            ...enter("fly-up", { duration: 800 }),
          }),
          device({
            ...PHONE_FRONT,
            x: 800,
            y: 1060,
            h: 460,
            shadow: DEEP_SHADOW,
            name: "iPhone",
            ...enter("fly-left", { duration: 1200 }),
          }),
        ],
      },
    ],
  },
  {
    id: "reel-dashboard-monitor",
    title: "Reel: Software-Dashboard",
    description: "Monitor mit deinem Dashboard, drei Kennzahlen-Karten ploppen nacheinander auf",
    presetId: "reel",
    topic: "produkt",
    style: "klar",
    tags: [
      "mockup",
      "dashboard",
      "software",
      "saas",
      "statistik",
      "monitor",
      "zahlen",
      "kennzahlen",
    ],
    previewTime: 3000,
    pages: [
      {
        background: "surface",
        nodes: [
          ...circles("primary/15", "accent/20"),
          stack({
            x: 80,
            y: 250,
            w: 920,
            align: "center",
            gap: 12,
            items: [
              t("SOFTWARE", "subline", 34, "primary", enter("fade")),
              t(
                "Alle Zahlen auf einen Blick",
                "heading-1",
                88,
                "text",
                enter("slide-up", { duration: 600 }),
              ),
            ],
          }),
          device({
            ...MONITOR,
            x: 60,
            y: 640,
            w: 960,
            shadow: DEVICE_SHADOW,
            name: "Monitor",
            ...enter("zoom", { duration: 800 }),
          }),
          ...(
            [
              { x: 40, y: 700, value: "+24 %", label: "Umsatz im Monat", icon: "trend-up" },
              { x: 700, y: 900, value: "1.280", label: "Neue Kunden", icon: "smiley" },
              { x: 80, y: 1180, value: "4,9", label: "Bewertung", icon: "star" },
            ] as const
          ).flatMap((card, index) => {
            const motion = during(1200 + index * 900, 16000, "pop", { out: null });
            return [
              rect({
                x: card.x,
                y: card.y,
                w: 340,
                h: 160,
                fill: "background",
                radius: "lg",
                shadow: { y: 16, blur: 40, color: "#0000003d" },
                name: card.label,
                ...motion,
              }),
              icon({
                icon: card.icon,
                x: card.x + 28,
                y: card.y + 28,
                w: 52,
                h: 52,
                color: "primary",
                ...motion,
              }),
              text({
                text: card.value,
                style: "heading-1",
                size: 56,
                color: "text",
                x: card.x + 96,
                y: card.y + 18,
                w: 220,
                h: 72,
                ...motion,
              }),
              text({
                text: card.label,
                style: "caption",
                size: 26,
                color: "text-muted",
                x: card.x + 28,
                y: card.y + 100,
                w: 290,
                ...motion,
              }),
            ];
          }),
        ],
      },
    ],
  },
];
